import { actionTypes as FirebaseActions } from 'react-redux-firebase';

import {
  CAMPAIGN_NAME,
  DELAYED_SHIPPING_EXPIRATION,
  DISABLE_SHIPMENT_TRACKING,
  HAS_LEGACY_CLAIM_PAGE_CONFIG,
  IS_TOGGLE_ON_HOLD_ENABLED,
  SEND_ON_BEHALF_THE_EMAIL,
  SEND_ON_HOLD,
} from '../../constants/bucket';
import { BUSINESS_REASON_REQUIRED, DISABLE_HANDWRITTEN_NOTE_STEP, ORG_ADDRESS_IDS } from '../../constants/campaigns';
import { SupportedFlowsEnum } from '../../constants/routing';
import {
  ALLOWED_DOMAINS,
  DISABLE_RECIPIENT_SHIPPED_EMAIL,
  DISABLE_SENDER_EMAIL_NOTIFICATIONS,
  ENABLED_2FA,
  IS_OFFICE_DROPSHIP_ENABLED,
  IS_RECEIVER_ADDRESS_FIXED,
  PASSWORD,
  RECIPIENTS,
  SHIPPING_OPTION,
  STATUS,
  TCRMForm,
  TShippingForm,
} from '../../constants/shipping';
import { INITIAL_ADD_EMAIL_FORM_STATE, INITIAL_ADD_NOTE_FORM_STATE } from '../../constants/templates';
import { IBucketState } from '../../types/bucket';
import { ICampaign, ICustomSend } from '../../types/campaigns';
import { TOneLinkRecipient } from '../../types/oneLink';
import { IReduxAction } from '../../types/redux';
import { ICommonAddress, ShippingOptionsEnum } from '../../types/shipping';
import { hasLegacyClaimPageConfig, isDigitalBox } from '../../utils/campaigns';
import { isItemDigital, isItemOnDemand } from '../../utils/inventories';
import * as AuthActions from '../actions/auth';
import * as BucketActions from '../actions/bucket';
import * as IntegrationsActions from '../actions/integrations';

export const initialState: IBucketState = {
  flow: null,
  bulk: {
    csv: null,
    result: null,
    [DISABLE_SHIPMENT_TRACKING]: false,
    isLoading: false,
    isSubmitted: false,
  },
  expiration_date: null,
  pre_created_engagement_id: '',
  engagement_source_type: '',
  business_reason_id: '',
  internal_notes: '',
  [SEND_ON_HOLD]: '',
  [DELAYED_SHIPPING_EXPIRATION]: '',
  [IS_TOGGLE_ON_HOLD_ENABLED]: false,
  [DISABLE_HANDWRITTEN_NOTE_STEP]: false,
  [SEND_ON_BEHALF_THE_EMAIL]: null,
  [PASSWORD]: '',
  [ALLOWED_DOMAINS]: [],
  [ENABLED_2FA]: false,
  [RECIPIENTS]: null,
  shippingDetails: {
    [STATUS]: '',
    [IS_RECEIVER_ADDRESS_FIXED]: false,
    [IS_OFFICE_DROPSHIP_ENABLED]: false,
    [ORG_ADDRESS_IDS]: undefined,
    receiver_address: undefined,
  },
  receiverDetails: null,
  [SHIPPING_OPTION]: ShippingOptionsEnum.Ground,
  note: INITIAL_ADD_NOTE_FORM_STATE,
  isNoteSkipped: true,
  email: INITIAL_ADD_EMAIL_FORM_STATE,
  pick_campaigns: null,
  items: [],
  isFetching: false,
  error: null,
  [DISABLE_SENDER_EMAIL_NOTIFICATIONS]: true,
  receiver_reminder_interval: '',
  [CAMPAIGN_NAME]: '',
  [DISABLE_RECIPIENT_SHIPPED_EMAIL]: false,
};

const reducer = (state: IBucketState = initialState, action: any): IBucketState => {
  switch (action.type) {
    case AuthActions.ADMIN_ROLE_CHANGED:
    case AuthActions.CHANGE_ORGANIZATION_SUCCESS:
    case FirebaseActions.LOGOUT: {
      return initialState;
    }
    case BucketActions.ADD_TO_BUCKET: {
      // TODO: Review this action handler during the CustomSend feature implementation and add TS to payload
      const itemToAdd = action.payload;
      const items = state.items.slice();

      const itemIndex = isItemOnDemand(itemToAdd)
        ? items.findIndex((i) => i.product_sku === itemToAdd.product_sku)
        : items.findIndex((i) => i.item_id === itemToAdd.item_id);
      const item = itemIndex === -1 ? undefined : items[itemIndex];

      if (item && !isItemDigital(item)) {
        // Replace rather than assign: state.items.slice() is shallow, so `item` is the very object
        // held by the previous state and writing to it mutates Redux state in place (EX-6331).
        items[itemIndex] = { ...item, quantity: item.quantity + itemToAdd.quantity };
      } else {
        items.push(itemToAdd);
      }
      return {
        ...state,
        items,
      };
    }
    case BucketActions.REMOVE_FROM_BUCKET: {
      const items = state.items.filter(
        (item) =>
          item.item_id
            ? item.item_id !== action.payload // InventoryItem
            : item.product_sku !== action.payload, // OnDemandItem
      );

      return {
        ...state,
        items,
      };
    }
    case BucketActions.CHANGE_BUCKET_ITEM_VALUE: {
      const { id, value, name } = action.payload;
      const itemToUpdate = state.items.find((item) => item.item_id === id);

      if (itemToUpdate) {
        return {
          ...state,
          items: state.items.map((item) =>
            item.item_id === itemToUpdate.item_id
              ? {
                  ...item,
                  [name]: value,
                }
              : item,
          ),
        };
      }

      return { ...state };
    }
    case BucketActions.ADD_SHIPPING_DETAILS_VALUE: {
      return {
        ...state,
        shippingDetails: {
          ...state.shippingDetails,
          ...action.payload,
        },
      };
    }
    case BucketActions.ADD_SHIPPING_VALUE: {
      return {
        ...state,
        receiverDetails: {
          ...state.receiverDetails,
          ...action.payload,
        },
      };
    }
    case BucketActions.ADD_SHIPPING_INFORMATION: {
      const { status, is_receiver_address_fixed, org_address_ids, receiver_address, ...contactFields } =
        (action as IReduxAction<TShippingForm & TCRMForm>).payload || ({} as TShippingForm & TCRMForm);

      const receiverDetails = { ...state.receiverDetails, ...contactFields };
      const shippingDetails = {
        ...state.shippingDetails,
        status,
        is_receiver_address_fixed,
        org_address_ids: org_address_ids as string[],
        receiver_address: receiver_address as ICommonAddress,
      };

      return {
        ...state,
        receiverDetails,
        shippingDetails,
      };
    }
    case BucketActions.SET_RECEIVER_ADDRESS: {
      return {
        ...state,
        shippingDetails: {
          ...state.shippingDetails,
          receiver_address: (action as IReduxAction<ICommonAddress>).payload,
          // Saving a fixed address via the sidebar is an unambiguous "Fixed" intent —
          // keep the flag aligned in case the selector default never dispatched it.
          is_receiver_address_fixed: true,
        },
      };
    }
    case BucketActions.ADD_NOTE: {
      return {
        ...state,
        note: action.payload,
      };
    }
    case BucketActions.TOGGLE_SKIP_NOTE: {
      return {
        ...state,
        ...action.payload,
      };
    }
    case BucketActions.ADD_EMAIL: {
      return {
        ...state,
        email: { ...state.email, ...action.payload },
      };
    }
    case BucketActions.FLUSH_BUCKET: {
      return { ...initialState };
    }
    case BucketActions.PRE_SELECTED_BOX_CONFIRMED: {
      const payload = action.payload as ICampaign | ICustomSend;

      const { name, box_id, items } = payload;
      // tslint:disable-next-line:variable-name
      const disable_handwritten_note_step =
        'disable_handwritten_note_step' in payload ? payload.disable_handwritten_note_step : false;
      // tslint:disable-next-line:variable-name
      const business_reason_required = 'business_reason_required' in payload ? payload.business_reason_required : false;
      // tslint:disable-next-line:variable-name
      const experience_id = 'experience_id' in payload ? payload.experience_id : undefined;

      return {
        ...state,
        campaign_name: name,
        pre_created_engagement_id: box_id,
        disable_handwritten_note_step,
        business_reason_required,
        experience_id,
        [HAS_LEGACY_CLAIM_PAGE_CONFIG]: 'org_id' in payload ? hasLegacyClaimPageConfig(payload) : false,
        items,
        pick_campaigns: null,
      };
    }
    case BucketActions.PICK_YOUR_GIFT_BOXES_CONFIRMED: {
      const campaigns = action.payload as ICampaign[];
      const physicalCampaigns = campaigns.filter((box) => !isDigitalBox(box));
      const isNoteCardStepDisabled = physicalCampaigns.every((campaign) => campaign[DISABLE_HANDWRITTEN_NOTE_STEP]);
      const isBusinessReasonRequired = campaigns.some((campaign: ICampaign) => campaign[BUSINESS_REASON_REQUIRED]);

      return {
        ...state,
        [DISABLE_HANDWRITTEN_NOTE_STEP]: isNoteCardStepDisabled,
        [BUSINESS_REASON_REQUIRED]: isBusinessReasonRequired,
      };
    }
    case BucketActions.UPDATE_SHIPPING_DETAILS: {
      return {
        ...state,
        shippingDetails: {
          ...state.shippingDetails,
          ...action.payload,
        },
      };
    }
    case BucketActions.RESET_BUCKET_ERRORS: {
      return {
        ...state,
        error: null,
      };
    }
    case IntegrationsActions.CHOOSE_SEARCH_ITEM: {
      return {
        ...state,
        receiverDetails: action.payload,
      };
    }
    case IntegrationsActions.SET_OPPORTUNITY: {
      return {
        ...state,
        receiverDetails: {
          ...state.receiverDetails,
          ...action.payload,
        },
      };
    }
    case BucketActions.UPLOAD_BULK_CSV: {
      return {
        ...state,
        bulk: {
          ...state.bulk,
          result: null,
        },
      };
    }
    case BucketActions.ADD_BULK_DATA: {
      return {
        ...state,
        bulk: {
          ...state.bulk,
          ...action.payload,
        },
      };
    }
    case BucketActions.CLEAR_BULK_DATA: {
      return {
        ...state,
        bulk: { ...initialState.bulk },
      };
    }
    case BucketActions.ADD_BUCKET_VALUE: {
      return {
        ...state,
        ...action.payload,
      };
    }
    case BucketActions.START_ENGAGEMENT_FLOW: {
      action = action as ReturnType<typeof BucketActions.startEngagementFlow>;

      const isBulkOrOneLinkFlow = [SupportedFlowsEnum.Bulk, SupportedFlowsEnum.OneLink].includes(action.payload);
      const isDirectSend = SupportedFlowsEnum.DirectSend === action.payload;
      const isBulk = SupportedFlowsEnum.Bulk === action.payload;

      return {
        ...state,
        flow: action.payload,
        // sender notifications are enabled by default in OneLink and Bulk flows
        [DISABLE_SENDER_EMAIL_NOTIFICATIONS]: isBulkOrOneLinkFlow,
        shippingDetails: {
          ...state.shippingDetails,
          // Fixed is the visual default for DirectSend and Bulk (status=''), so keep state in sync.
          is_receiver_address_fixed: isDirectSend || isBulk,
          // Office dropship is tied to an Address Book selection that does not survive a flow
          // switch, so a flag left over from a previous session would be submitted with no
          // toggle on screen to clear it.
          [IS_OFFICE_DROPSHIP_ENABLED]: false,
        },
      };
    }
    case BucketActions.TOGGLE_PICK_YOUR_GIFT: {
      return {
        ...state,
        campaign_name: '',
        pick_campaigns: action.payload ? [] : null,
        experience_id: undefined,
        [HAS_LEGACY_CLAIM_PAGE_CONFIG]: undefined,
      };
    }
    case BucketActions.UPLOAD_2FA_RECIPIENTS: {
      const { successfulRecipients, failedRecipients } = action.payload;

      return {
        ...state,
        recipients: {
          successfulRecipients: state?.recipients?.successfulRecipients
            ? [...successfulRecipients, ...state.recipients.successfulRecipients]
            : successfulRecipients,
          failedRecipients,
        },
      };
    }
    case BucketActions.DELETE_ALL_ALLOWED_RECIPIENTS: {
      return {
        ...state,
        recipients: {
          successfulRecipients: [],
          failedRecipients: [],
        },
      };
    }
    case BucketActions.DELETE_ALLOWED_RECIPIENT: {
      return {
        ...state,
        recipients: {
          successfulRecipients: state.recipients?.successfulRecipients?.filter(
            (recipient: TOneLinkRecipient) => recipient.id !== action.payload,
          ),
        },
      };
    }
    case BucketActions.EDIT_ALLOWED_RECIPIENT: {
      const { id } = action.payload;
      const successfulRecipients = state?.recipients?.successfulRecipients
        ? [...state.recipients.successfulRecipients]
        : [];

      const updatedRecipientIndex = successfulRecipients?.findIndex(
        (recipient: TOneLinkRecipient) => recipient.id === id,
      );
      if (typeof updatedRecipientIndex !== 'undefined') {
        successfulRecipients[updatedRecipientIndex] = action.payload;
      }

      return {
        ...state,
        recipients: {
          successfulRecipients,
        },
      };
    }
    case BucketActions.ADD_ALLOWED_RECIPIENT: {
      const newRecipient = action.payload;

      return {
        ...state,
        recipients: {
          successfulRecipients: state?.recipients?.successfulRecipients
            ? [newRecipient, ...state.recipients.successfulRecipients]
            : [newRecipient],
        },
      };
    }
    case BucketActions.SET_PICK_YOUR_GIFT: {
      const items = (() => {
        const pickCampaigns = state.pick_campaigns?.slice();
        const { box_id: payloadBoxId } = action.payload;

        if (!pickCampaigns || !pickCampaigns.length) {
          return [action.payload];
        }

        const searchCampaign =
          (search: boolean) =>
          ({ box_id }: ICampaign) =>
            search ? box_id === payloadBoxId : box_id !== payloadBoxId;
        const isExistCampaign = pickCampaigns.some(searchCampaign(true));

        // If campaign exists, it's removed from the pick_campaigns array. In other case it's added
        return isExistCampaign ? pickCampaigns.filter(searchCampaign(false)) : [...pickCampaigns, action.payload];
      })();

      return {
        ...state,
        pick_campaigns: items,
        pre_created_engagement_id: '',
        items: [],
        experience_id: undefined,
        [HAS_LEGACY_CLAIM_PAGE_CONFIG]: undefined,
      };
    }
    case BucketActions.RESET_CUSTOM_SEND: {
      return {
        ...state,
        items: [],
        pre_created_engagement_id: '',
        experience_id: undefined,
        [HAS_LEGACY_CLAIM_PAGE_CONFIG]: undefined,
      };
    }

    default:
      return state;
  }
};

export default reducer;
